import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PrintableTicket from '../../common/PrintableTicket';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f5f5f5;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  @media print {
    background: white;
    padding: 0;
  }
`;

const ControlBar = styled.div`
  width: 100%;
  max-width: 800px;
  margin-bottom: 1.5rem;
  display: flex;
  gap: 1rem;
  justify-content: center;
  
  @media print {
    display: none;
  }
`;

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 6px;
  font-size: 0.95rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  
  ${props => props.variant === 'primary' ? `
    background: #192f6a;
    color: white;
    
    &:hover {
      background: #2a4a9a;
    }
  ` : `
    background: white;
    color: #192f6a;
    border: 1px solid #192f6a;
    
    &:hover {
      background: #f8f9fa;
    }
  `}
  
  &:active {
    transform: scale(0.98);
  }
`;

// Helper function to create fallback data from URL params
const createFallbackData = (params) => {
  const dateParam = params.get('date');
  const timeParam = params.get('time');
  const ticketsParam = params.get('tickets');
  const amountParam = params.get('amount');
  const nameParam = params.get('name');
  const ref = params.get('ref');
  
  const ticketCount = Math.max(parseInt(ticketsParam || '1', 10) || 1, 1);
  const totalAmount = amountParam ? parseFloat(amountParam) : undefined;
  const perTicket = totalAmount && ticketCount > 0 ? Number((totalAmount / ticketCount).toFixed(2)) : undefined;

  return {
    bookingReference: ref,
    selectedTickets: [
      {
        name: 'Admission Ticket',
        quantity: ticketCount,
        price: perTicket
      }
    ],
    selectedDate: dateParam ? new Date(dateParam).toISOString() : new Date().toISOString(),
    selectedTimeSlot: timeParam ? { time: timeParam } : {},
    customerDetails: nameParam ? {
      firstName: nameParam.split(' ')[0] || '',
      lastName: nameParam.split(' ').slice(1).join(' ') || ''
    } : {},
    totalAmount: totalAmount,
    venposBasket: null,
    bookingSummary: null,
    isFamilySaver: false
  };
};

const TicketPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [ticketData, setTicketData] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);

  useEffect(() => {


    // Get storage key from URL
    const params = new URLSearchParams(location.search);
    const storageKey = params.get('key');


    if (storageKey) {
      // Get data from localStorage
      const storedData = localStorage.getItem(storageKey);


      if (storedData) {
        try {
          const parsed = JSON.parse(storedData);


          // Ensure selectedDate is valid
          if (parsed.selectedDate) {

            setTicketData(parsed);
          } else {

            setTicketData(null);
          }
        } catch (e) {


          setTicketData(null);
        }
      } else {


        setTicketData(null);
      }
    } else {

      // Try to fetch booking data from WordPress by booking reference
      const ref = params.get('ref');

      if (ref) {

        // Determine WordPress API endpoint based on environment
        const hostname = window.location.hostname;
        let apiEndpoint;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          apiEndpoint = 'http://localhost:8888/Website/wordpress/wp-json';
        } else if (hostname.includes('staging.belvoircastle.com')) {
          apiEndpoint = 'https://staging.belvoircastle.com/wp/wp-json';
        } else {
          apiEndpoint = 'https://www.belvoircastle.com/wp/wp-json';
        }
        
        fetch(`${apiEndpoint}/custom/v1/get-booking-data/${ref}`)
          .then(response => response.json())
          .then(data => {
            if (data.success && data.data) {

              // Ensure selectedDate is a Date object
              if (data.data.selectedDate && typeof data.data.selectedDate === 'string') {
                data.data.selectedDate = data.data.selectedDate; // Keep as ISO string for PrintableTicket
              }
              
              setTicketData(data.data);
              setIsLoading(false);
            } else {

              setTicketData(createFallbackData(params));
              setIsLoading(false);
            }
          })
          .catch(error => {

            setTicketData(createFallbackData(params));
            setIsLoading(false);
          });
        
        // Don't set loading to false here - wait for the fetch
        return;
      } else {

        setTicketData(null);
      }
    }
    
    setIsLoading(false);
  }, [location.search]);

  const handlePrint = () => {
    window.print();
  };

  const handleClose = () => {
    window.close();
  };

  if (isLoading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '2rem' }}>
          <h2>Loading ticket...</h2>
        </div>
      </PageContainer>
    );
  }

  if (!ticketData) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '2rem', color: '#192f6a' }}>
          <h2>No ticket data available</h2>
          <p>Please return to your booking confirmation and try again.</p>
          <Button onClick={handleClose} style={{ marginTop: '1rem' }}>
            Close Window
          </Button>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ControlBar>
        <Button variant="primary" onClick={handlePrint}>
          Print Ticket
        </Button>
        <Button onClick={handleClose}>
          Close Window
        </Button>
      </ControlBar>

      {ticketData && ticketData.selectedDate && (
        <PrintableTicket
          bookingReference={ticketData.bookingReference}
          selectedTickets={ticketData.selectedTickets}
          selectedDate={new Date(ticketData.selectedDate)}
          selectedTimeSlot={ticketData.selectedTimeSlot}
          customerDetails={ticketData.customerDetails}
          totalAmount={ticketData.totalAmount}
          venposBasket={ticketData.venposBasket}
          bookingSummary={ticketData.bookingSummary}
          isFamilySaver={ticketData.isFamilySaver}
        />
      )}
    </PageContainer>
  );
};

export default TicketPage;

